import { useContext, useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import { appleAuthHelpers } from 'react-apple-signin-auth';
import { sendGAEvent } from '@next/third-parties/google';
import useTranslation from 'next-translate/useTranslation';
import { StoreContext } from '../store';
import config from '../config';

/** Apple Signin button with enhanced security */
const AppleAuthButton = ({ onSuccess, onError, buttonText = 'Sign up by Apple', isSignUp = false, handleLoginStatus = null }) => {
  const { t } = useTranslation('common');
  const store = useContext(StoreContext);
  const [isLoading, setIsLoading] = useState(false);

  // Load Apple SDK script manually with error handling
  useEffect(() => {
    const loadAppleScript = () => {
      if (window.AppleID) return; // Already loaded

      const script = document.createElement('script');
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      
      // Security: Add integrity check if possible
      script.crossOrigin = 'anonymous';
      
      document.head.appendChild(script);
    };

    loadAppleScript();
  }, []);

  // Cryptographically secure nonce generation
  const generateSecureNonce = () => {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      try {
        // Generate 128-bit random nonce (recommended by Apple)
        const array = new Uint32Array(4);
        window.crypto.getRandomValues(array);
        return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
      } catch (error) {
        // console.warn('Crypto API failed, using fallback nonce generation');
      }
    }
    
    // Secure fallback for older browsers
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    const extraRandom = Math.random().toString(36).substring(2, 10);
    return `nonce_${timestamp}_${randomPart}_${extraRandom}`;
  };

  // Cryptographically secure state generation
  const generateSecureState = () => {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      try {
        const randomBytes = new Uint8Array(32);
        window.crypto.getRandomValues(randomBytes);
        const base64String = btoa(String.fromCharCode(...randomBytes));
        // Make URL-safe
        return 'state_' + base64String.replace(/[+/=]/g, (match) => {
          switch (match) {
            case '+': return '-';
            case '/': return '_';
            case '=': return '';
            default: return match;
          }
        });
      } catch (error) {
        // console.warn('Crypto API failed, using fallback state generation');
      }
    }
    
    // Secure fallback
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    const extraRandom = Math.random().toString(36).substring(2, 10);
    return `state_${timestamp}_${randomPart}_${extraRandom}`;
  };

  // Basic ID token validation (client-side)
  const validateIdToken = (idToken) => {
    if (!idToken) return false;
    
    try {
      // Basic JWT structure validation
      const parts = idToken.split('.');
      if (parts.length !== 3) {
        // console.warn('Invalid JWT structure');
        return false;
      }
      
      // Decode payload (without verification - that's done server-side)
      const payload = JSON.parse(atob(parts[1]));
      
      // Check expiration
      if (payload.exp && payload.exp < Date.now() / 1000) {
        // console.warn('ID token expired');
        return false;
      }
      
      // Check issuer
      if (payload.iss !== 'https://appleid.apple.com') {
        // console.warn('Invalid issuer');
        return false;
      }
      
      // Check audience (should be your client ID)
      const expectedClientId = config.APPLE_CLIENT_ID;
      if (expectedClientId && payload.aud !== expectedClientId) {
        // console.warn('Invalid audience');
        return false;
      }
      
      return true;
    } catch (error) {
      // console.error('Token validation error:', error);
      return false;
    }
  };

  // Get secure redirect URI
  const getSecureRedirectURI = () => {
    if (config.NODE_ENV === 'production') {
      return config.APPLE_REDIRECT_URI || process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI || 'https://app.superkos.id';
    }
    
    // Development: Ensure we use localhost or secure connection
    if (typeof window !== 'undefined') {
      const { protocol, hostname, port } = window.location;
      
      // Force HTTPS in production-like environments
      if (hostname !== 'localhost' && hostname !== '127.0.0.1' && protocol !== 'https:') {
        // console.warn('Apple Sign-In requires HTTPS in production');
        return `https://${hostname}${port ? ':' + port : ''}`;
      }
      
      return window.location.origin;
    }
    
    return 'http://localhost:3000';
  };

  const signUpWithApple = async () => {
    // Prevent multiple simultaneous requests
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      // Check if Apple SDK is loaded
      if (!window.AppleID) {
        throw new Error('Apple SDK not loaded yet. Please try again in a moment.');
      }

      // Validate required configuration
      const clientId = config.APPLE_CLIENT_ID || process.env.NEXT_PUBLIC_APPLE_CLIENT_ID;
      if (!clientId) {
        throw new Error('Apple Client ID not configured');
      }
      
      const nonce = generateSecureNonce();
      const state = generateSecureState();
      const redirectURI = getSecureRedirectURI();
      
      const response = await appleAuthHelpers.signIn({
        authOptions: {
          clientId: clientId,
          scope: 'email name',
          state: state,
          nonce: nonce,
          usePopup: true,
          redirectURI: redirectURI,
        }
      });

      // Validate response structure
      if (!response || !response.authorization) {
        throw new Error('Invalid response from Apple');
      }

      // Validate ID token if present
      if (response.authorization.id_token) {
        if (!validateIdToken(response.authorization.id_token)) {
          throw new Error('Invalid ID token received from Apple');
        }
      }

      // Validate authorization code
      if (!response.authorization.code) {
        throw new Error('No authorization code received from Apple');
      }

      if (isSignUp) {
        // Send Google Analytics event
        try {
          sendGAEvent({ event: "conversion", value: "user_sign_up_apple" });
        } catch (gaError) {
          // console.warn('Google Analytics error:', gaError);
          // Don't fail the auth flow for analytics errors
        }
      }

      // Extract user data from Apple response
      const userData = {
        access_token: response.authorization.id_token,
        client_id: clientId,
        provider: 'Apple',
      };

      // Call parent success handler if provided, otherwise use store directly
      if (onSuccess) {
        await onSuccess(userData);
      } else {
        // Call your backend API to handle Apple signup
        const signupResponse = await store.user.signInApple(userData);
        
        // Handle the response (this function should be passed as a prop or defined in parent)
        if (typeof handleLoginStatus === 'function') {
          await handleLoginStatus(signupResponse.status, signupResponse.data?.message);
        } else {
          // Basic success handling
          if (signupResponse.status === 200) {
            store.pushToastMessage({
              message: t('Successfully signed in with Apple'),
              severity: 'success',
            });
          } else {
            throw new Error(signupResponse.data?.message || 'Authentication failed');
          }
        }
      }

    } catch (error) {
      // console.error('Apple Sign-In error:', error);
      
      // Sanitize error message for user display
      let userMessage;
      if (error.message?.includes('SDK not loaded')) {
        userMessage = t('Please wait a moment and try again');
      } else if (error.message?.includes('popup')) {
        userMessage = t('Sign-in was cancelled or blocked. Please try again');
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        userMessage = t('Network error. Please check your connection and try again');
      } else {
        userMessage = t('Apple sign-in failed. Please try again');
      }
      
      const sanitizedError = new Error(userMessage);
      
      if (onError) {
        onError(sanitizedError);
      } else {
        store.pushToastMessage({
          message: userMessage,
          severity: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const AppleIcon = () => {
    return (
      <svg role="img" x="0px" y="0px" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <title>Apple</title>
        <path fill='#fff' d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
      </svg>
    );
  };

  return (
    <Button 
      fullWidth 
      onClick={signUpWithApple}
      disabled={isLoading}
      style={{ 
        color: "white", 
        backgroundColor: isLoading ? "#666" : "#1f1f1f",
        opacity: isLoading ? 0.7 : 1
      }}
      variant="contained"
      startIcon={isLoading ? null : <AppleIcon />}
    >
      {isLoading ? t('Signing in...') : t(buttonText)}
    </Button>
  );
};

export default AppleAuthButton;